
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const BeginningStateExtractionDiagnostic = () => {
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const runDiagnostic = async () => {
    try {
      setLoading(true);
      console.log('🔍 Running diagnostic for state names at beginning of report names...');

      // Test cases from the screenshot - reports that start with state names
      const stateReportPatterns = [
        'Iowa Daily Cash Grain Bids',
        'North Carolina Cash Grain Bids', 
        'South Dakota Daily Grain Bids',
        'Oklahoma Daily Grain Bids',
        'Kansas Daily Grain Bids',
        'Arkansas Daily Grain Bids'
      ];

      // Get actual records that match these patterns
      const { data: matchingReports, error: reportsError } = await supabase
        .from('usda_reports')
        .select('id, report_name, report_state, location_type, location_name')
        .or(
          stateReportPatterns.map(pattern => 
            `report_name.ilike.${pattern}*`
          ).join(',')
        )
        .limit(20);

      if (reportsError) {
        throw reportsError;
      }

      // Test location parsing function on these specific patterns
      const locationParsingResults = [];
      for (const testPattern of stateReportPatterns) {
        try {
          const { data: locationResult, error: locationError } = await supabase
            .rpc('parse_location_from_report_name', {
              report_name_input: testPattern
            });

          locationParsingResults.push({
            input: testPattern,
            locationResult,
            locationError: locationError?.message
          });
        } catch (err) {
          locationParsingResults.push({
            input: testPattern,
            locationResult: null,
            locationError: 'Function call failed: ' + (err as Error).message
          });
        }
      }

      // Get statistics on reports starting with state names
      const { data: stateStartingReports, error: statsError } = await supabase
        .from('usda_reports')
        .select('report_name, report_state, location_type, location_name')
        .or('report_name.ilike.iowa %,report_name.ilike.north carolina %,report_name.ilike.south dakota %,report_name.ilike.oklahoma %,report_name.ilike.kansas %,report_name.ilike.arkansas %,report_name.ilike.missouri %,report_name.ilike.tennessee %,report_name.ilike.kentucky %')
        .limit(50);

      // Test direct pattern matching that should work
      const directPatternTests = stateReportPatterns.map(pattern => {
        const lowerPattern = pattern.toLowerCase();
        let extractedState = null;
        
        // Test basic state extraction patterns
        if (lowerPattern.startsWith('iowa ')) extractedState = 'IA';
        else if (lowerPattern.startsWith('north carolina ')) extractedState = 'NC';
        else if (lowerPattern.startsWith('south dakota ')) extractedState = 'SD';
        else if (lowerPattern.startsWith('oklahoma ')) extractedState = 'OK';
        else if (lowerPattern.startsWith('kansas ')) extractedState = 'KS';
        else if (lowerPattern.startsWith('arkansas ')) extractedState = 'AR';
        
        return {
          input: pattern,
          extractedState,
          shouldWork: extractedState !== null
        };
      });

      const diagnosticResults = {
        matchingReports: matchingReports || [],
        locationParsingResults,
        directPatternTests,
        stateStartingReports: stateStartingReports || [],
        nullStateCount: stateStartingReports?.filter(r => r.report_state === null).length || 0,
        validStateCount: stateStartingReports?.filter(r => r.report_state !== null).length || 0,
        timestamp: new Date().toISOString(),
        reportsError: reportsError?.message,
        statsError: statsError?.message
      };

      setDiagnosticData(diagnosticResults);
      console.log('📊 Beginning state diagnostic results:', diagnosticResults);

      toast({
        title: "State Beginning Diagnostic Complete",
        description: "Analysis of state extraction for reports starting with state names",
      });

    } catch (error: any) {
      console.error('❌ Beginning state diagnostic error:', error);
      toast({
        title: "Diagnostic Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const forceUpdateStateBeginningReports = async () => {
    try {
      setLoading(true);
      console.log('🔧 Force updating reports that start with state names...');

      // Direct updates for specific state patterns
      const stateUpdates = [
        { pattern: 'iowa %', state: 'IA' },
        { pattern: 'north carolina %', state: 'NC' },
        { pattern: 'south dakota %', state: 'SD' },
        { pattern: 'oklahoma %', state: 'OK' },
        { pattern: 'kansas %', state: 'KS' },
        { pattern: 'arkansas %', state: 'AR' },
        { pattern: 'missouri %', state: 'MO' },
        { pattern: 'tennessee %', state: 'TN' },
        { pattern: 'kentucky %', state: 'KY' },
        { pattern: 'california %', state: 'CA' },
        { pattern: 'texas %', state: 'TX' },
        { pattern: 'florida %', state: 'FL' },
        { pattern: 'georgia %', state: 'GA' }
      ];

      const results = [];
      for (const update of stateUpdates) {
        const { data, error } = await supabase
          .from('usda_reports')
          .update({ 
            report_state: update.state,
            location_type: 'State',
            location_name: update.pattern.replace(' %', '').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
          })
          .ilike('report_name', update.pattern)
          .is('report_state', null)
          .select('id, report_name, report_state');

        results.push({
          pattern: update.pattern,
          state: update.state,
          updatedCount: data?.length || 0,
          error: error?.message
        });

        console.log(`Updated ${data?.length || 0} reports for pattern "${update.pattern}"`);
      }

      const totalUpdated = results.reduce((sum, r) => sum + r.updatedCount, 0);
      
      toast({
        title: "State Beginning Force Update Complete",
        description: `Updated ${totalUpdated} reports that start with state names`,
      });

      console.log('✅ State beginning force update results:', results);
      
      // Re-run diagnostic
      await runDiagnostic();

    } catch (error: any) {
      console.error('❌ State beginning force update error:', error);
      toast({
        title: "Force Update Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-6xl">
      <CardHeader>
        <CardTitle className="text-blue-600">🎯 State Beginning Extraction Diagnostic</CardTitle>
        <p className="text-sm text-gray-600">
          Analyze why reports starting with state names (Iowa, North Carolina, etc.) have NULL report_state
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <Button 
            onClick={runDiagnostic}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Running Diagnostic...' : '🔍 Diagnose State Beginning Reports'}
          </Button>
          
          <Button 
            onClick={forceUpdateStateBeginningReports}
            disabled={loading}
            variant="outline"
            className="border-green-500 text-green-600 hover:bg-green-50"
          >
            {loading ? 'Force Updating...' : '🔧 Force Update State Beginning Reports'}
          </Button>
        </div>

        {diagnosticData && (
          <div className="space-y-6">
            {/* Statistics */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Statistics for State Beginning Reports:</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>Reports with NULL report_state:</strong> {diagnosticData.nullStateCount}
                </div>
                <div>
                  <strong>Reports with valid report_state:</strong> {diagnosticData.validStateCount}
                </div>
              </div>
            </div>

            {/* Direct Pattern Test Results */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Direct Pattern Matching Test:</h3>
              <div className="space-y-3">
                {diagnosticData.directPatternTests.map((test: any, index: number) => (
                  <div key={index} className="border-b pb-2">
                    <div><strong>Input:</strong> {test.input}</div>
                    <div><strong>Extracted State:</strong> {test.extractedState || 'NULL'}</div>
                    <div><strong>Should Work:</strong> {test.shouldWork ? '✅ Yes' : '❌ No'}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Parsing Results */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Location Parsing Function Results:</h3>
              <div className="space-y-2">
                {diagnosticData.locationParsingResults.map((test: any, index: number) => (
                  <div key={index} className="text-sm border-b pb-2">
                    <div><strong>Input:</strong> {test.input}</div>
                    <div><strong>Result:</strong> {JSON.stringify(test.locationResult) || 'NULL'}</div>
                    {test.locationError && <div className="text-red-600"><strong>Error:</strong> {test.locationError}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Actual Database Records */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Current Database State (Sample Records):</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {diagnosticData.stateStartingReports.map((report: any, index: number) => (
                  <div key={index} className="text-sm border-b pb-2">
                    <div><strong>Name:</strong> {report.report_name}</div>
                    <div><strong>State:</strong> {report.report_state || 'NULL'}</div>
                    <div><strong>Location Type:</strong> {report.location_type || 'NULL'}</div>
                    <div><strong>Location Name:</strong> {report.location_name || 'NULL'}</div>
                  </div>
                ))}
              </div>
            </div>

            {(diagnosticData.reportsError || diagnosticData.statsError) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">❌ Errors:</h4>
                {diagnosticData.reportsError && <p className="text-sm text-red-700">Reports Error: {diagnosticData.reportsError}</p>}
                {diagnosticData.statsError && <p className="text-sm text-red-700">Stats Error: {diagnosticData.statsError}</p>}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
